import React, { useEffect } from "react";
import { createPortal } from "react-dom";

interface AppDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}

const AppDialog: React.FC<AppDialogProps> = ({ open, title, onClose, children, footer, wide = false }) => {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="app-dialog-backdrop" onClick={onClose}>
      <div
        className={`app-dialog ${wide ? "app-dialog--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="app-dialog__header">
          <h2 id="app-dialog-title">{title}</h2>
          <button className="app-dialog__close" onClick={onClose} aria-label="Close dialog">
            x
          </button>
        </div>
        <div className="app-dialog__body">{children}</div>
        {footer && <div className="app-dialog__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default AppDialog;
